import React, { useEffect, useState } from "react";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";

import dayjs from "dayjs";
import { MAIN_URL } from "../../../Configurations/Urls";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Paper,
  Grid,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Avatar,
  Stack,
  Divider,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Snackbar,
  Alert
} from "@mui/material";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import GroupIcon from "@mui/icons-material/Group";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { DateCalendar, PickersDay } from "@mui/x-date-pickers";

import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { useNavigate } from "react-router-dom";

export default function MassCopy({ onNext }) {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();

  // Snackbar state for toast
  const [toast, setToast] = useState({open: false, message: "", severity: "success"});

  // Keep selection as IDs only (never mix objects + IDs)
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Each selected task id maps to copy rows [{ targetDate, newTitle }]
  const [copyRows, setCopyRows] = useState({});
  const [activeConfigTaskId, setActiveConfigTaskId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [selectOptions, setSelectOptions] = useState({
    Project: [],
    Employees: [],
  });

  // Person type: 1 = Employee, 2 = Intern
  const [employeeType, setEmployeeType] = useState("1"); // Default to Employee

  const [selectedOptions, setSelectedOptions] = useState({
    Project: [],
    Employees: [],
    timePeriod: "lastWeek",
  });

  const [customDates, setCustomDates] = useState({
    from: "",
    to: "",
  });
  const [calendarViewDate, setCalendarViewDate] = useState(dayjs());

  const getDateRange = () => {
    const today = dayjs();

    if (selectedOptions.timePeriod === "lastWeek") {
      const start = today.subtract(7, "day").format("YYYY-MM-DD");
      const end = today.format("YYYY-MM-DD");
      return { start, end };
    }

    if (selectedOptions.timePeriod === "lastMonth") {
      const start = today.subtract(1, "month").format("YYYY-MM-DD");
      const end = today.format("YYYY-MM-DD");
      return { start, end };
    }

    if (selectedOptions.timePeriod === "custom") {
      const start = customDates.from ? dayjs(customDates.from).format("YYYY-MM-DD") : "";
      const end = customDates.to ? dayjs(customDates.to).format("YYYY-MM-DD") : "";
      return { start, end };
    }

    return { start: "", end: "" };
  };

  const validateCustomDateRange = () => {
    if (selectedOptions.timePeriod !== "custom") return true;

    const { start, end } = getDateRange();
    if (!start && !end) {
      alert("Please select From and/or To dates for custom period.");
      return false;
    }

    if (start && end && dayjs(start).isAfter(dayjs(end))) {
      alert("From date cannot be after To date.");
      return false;
    }

    return true;
  };

  const fetchTasks = async () => {
    try {
      const { start, end } = getDateRange();

      if (!org?.organization_id) {
        console.error("Organization is missing in session.");
        return;
      }

      if (!validateCustomDateRange()) return;

      const normalizedEmployeeIds = (selectedOptions.Employees || [])
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v));

      const normalizedProjectIds = (selectedOptions.Project || [])
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v));

      const params = {
        organization_id: org.organization_id,
        person_type_id: employeeType, // Filter by selected employee type
        ...(normalizedEmployeeIds.length
          ? { organization_person_ids: normalizedEmployeeIds }
          : {}),
        ...(normalizedProjectIds.length
          ? { organization_project_ids: normalizedProjectIds }
          : {}),
        per_page: 100,
        page: 1,
      };

      if (start) {
        params.start_date = start;
      }
      if (end) {
        params.end_date = end;
      }

      const response = await axios.get(
        `${MAIN_URL}/api/project-management/project-tasks/employee-tasks-v1`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params,
        }
      );
console.log("task",response.data.data?.data)
      setTasks(response.data.data?.data || []);
      // Task list changed, clear selection + copy rows to avoid mismatched IDs.
      setSelectedTaskIds([]);
      setCopyRows({});
    } catch (e) {
      console.error("Failed to load tasks");
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(
        `${MAIN_URL}/api/project-management/projects`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org?.organization_id,
          },
        }
      );

      const sortedProjects = [...response.data.data].sort((a, b) =>
        (a.project_name || "").localeCompare(b.project_name || "")
      );

      setSelectOptions((prev) => ({
        ...prev,
        Project: sortedProjects,
      }));
    } catch {
      console.error("Failed to load projects");
    }
  };

  const fetchEmployeesOfOrg = async () => {
    try {
      const response = await axios.get(
        `${MAIN_URL}/api/organizations/${org.organization_id}/persons-data-all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Show Employees or Interns based on current employeeType selection
      let persons = (response.data.persons || []).filter(
        (person) => String(person.organization_person?.person_type_id) === String(employeeType)
      );

      const sortedPersons = [...persons].sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        )
      );

      setSelectOptions((prev) => ({
        ...prev,
        Employees: sortedPersons,
      }));
      // If selected employees not in filtered list, clear invalid selections
      setSelectedOptions((prev) => ({
        ...prev,
        Employees: prev.Employees.filter(id =>
          sortedPersons.some(person => person.organization_person_id === id)
        )
      }));
    } catch {
      console.error("Failed to load Employees/Interns");
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchEmployeesOfOrg();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // When employeeType changes, fetch relevant employees/interns
    fetchEmployeesOfOrg();
  }, [employeeType]);

  useEffect(() => {
    // Leaving custom mode should reset custom dates to prevent stale filtering.
    if (selectedOptions.timePeriod !== "custom") {
      setCustomDates({ from: "", to: "" });
    }
  }, [selectedOptions.timePeriod]);

  const handleToggleTask = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );

    setCopyRows((prev) => {
      const task = tasks.find((t) => t.organization_project_task_id === taskId);
      const defaultTitle = task?.task_title || "";
      const isSelected = selectedTaskIds.includes(taskId);

      // If task gets unselected, remove its rows to keep state tidy.
      if (isSelected) {
        if (!prev?.[taskId]) return prev;
        const next = { ...prev };
        delete next[taskId];
        return next;
      }

      // Initialize first row on selection.
      if (prev?.[taskId]?.length) return prev;
      return {
        ...prev,
        [taskId]: [{ targetDate: "", newTitle: defaultTitle }],
      };
    });
  };

  const parseScheduledDate = (d) => {
    if (!d) return null;
    if (typeof d !== "string") return d ? dayjs(d) : null;
    const parts = d.includes("/") ? d.split("/") : d.split("-");
    if (parts.length === 3) {
      const [first, second, third] = parts;
      const year = third.length === 4 ? third : first.length === 4 ? first : null;
      const month = second?.padStart(2, "0");
      const day = (year === third ? first : third)?.padStart(2, "0");
      if (year) return dayjs(`${year}-${month}-${day}`);
    }
    return dayjs(d);
  };

  const handleCopyRowChange = (taskId, idx, field, value) => {
    setCopyRows((prev) => {
      const rows = Array.isArray(prev[taskId]) ? [...prev[taskId]] : [];
      if (!rows[idx]) rows[idx] = { targetDate: "", newTitle: "" };
      rows[idx] = { ...rows[idx], [field]: value };
      return {
        ...prev,
        [taskId]: rows,
      };
    });
  };

  const handleAddCopyRow = (taskId) => {
    const task = tasks.find((t) => t.organization_project_task_id === taskId);
    setCopyRows((prev) => {
      const rows = Array.isArray(prev[taskId]) ? prev[taskId] : [];
      return {
        ...prev,
        [taskId]: [...rows, { targetDate: "", newTitle: task?.task_title || "" }],
      };
    });
  };

  const handleRemoveCopyRow = (taskId, idx) => {
    setCopyRows((prev) => {
      const rows = Array.isArray(prev[taskId]) ? [...prev[taskId]] : [];
      rows.splice(idx, 1);
      return {
        ...prev,
        [taskId]: rows.length ? rows : [{ targetDate: "", newTitle: "" }],
      };
    });
  };

  const handleToggleCalendarDate = (taskId, dateValue) => {
    if (!taskId || !dateValue) return;
    const task = tasks.find((t) => t.organization_project_task_id === taskId);
    const minDate = parseScheduledDate(task?.scheduled_date);
    if (minDate && dayjs(dateValue).isBefore(minDate, "day")) return;

    const formattedDate = dayjs(dateValue).format("YYYY-MM-DD");
    setCopyRows((prev) => {
      const rows = Array.isArray(prev[taskId]) ? [...prev[taskId]] : [];
      const existingIndex = rows.findIndex((row) => row?.targetDate === formattedDate);

      if (existingIndex > -1) {
        rows.splice(existingIndex, 1);
        return {
          ...prev,
          [taskId]: rows,
        };
      }

      // If there's an empty target-date row, use it first (prevents skipping first date).
      const firstEmptyRowIndex = rows.findIndex((row) => !row?.targetDate);
      if (firstEmptyRowIndex > -1) {
        rows[firstEmptyRowIndex] = {
          ...rows[firstEmptyRowIndex],
          targetDate: formattedDate,
          newTitle: rows[firstEmptyRowIndex]?.newTitle || task?.task_title || "",
        };
        return {
          ...prev,
          [taskId]: rows,
        };
      }

      return {
        ...prev,
        [taskId]: [
          ...rows,
          {
            targetDate: formattedDate,
            newTitle: task?.task_title || "",
          },
        ],
      };
    });
  };

  const openTaskConfig = (taskId) => {
    setActiveConfigTaskId(taskId);
    setIsConfirmOpen(true);
  };

  const areAllCopyRowsValid = () => {
    return (
      selectedTaskIds.length > 0 &&
      selectedTaskIds.every(
        (id) =>
          Array.isArray(copyRows[id]) &&
          copyRows[id].length > 0 &&
          copyRows[id].every((row) => !!row?.targetDate && !!row?.newTitle?.trim())
      )
    );
  };

  const handleConfirmCopy = () => {
    // Confirm all selected IDs have at least one row with both date and title.
    const missingFields = selectedTaskIds.some(
      (id) =>
        !Array.isArray(copyRows[id]) ||
        copyRows[id].length === 0 ||
        copyRows[id].some((row) => !row?.targetDate || !row?.newTitle?.trim())
    );

    if (missingFields) {
      alert("Please add target date and task title for each selected copy row.");
      return;
    }

    // Validate that no target date is before the scheduled date of its task
    const taskById = Object.fromEntries(tasks.map((t) => [t.organization_project_task_id, t]));
    const invalidTask = selectedTaskIds.find((id) => {
      const task = taskById[id];
      const scheduled = parseScheduledDate(task?.scheduled_date);
      const rows = copyRows[id] || [];
      return rows.some((row) => {
        const dateStr = row?.targetDate;
        if (!dateStr) return true; // Guard, already filtered above but just in case
        const target = dayjs(dateStr);
        return scheduled && target.isBefore(scheduled, "day");
      });
    });
    if (invalidTask) {
      alert("One or more target dates are before the scheduled date. Please correct the date(s).");
      return;
    }

    const token = localStorage.getItem("token");
    // Payload: for each task id, include per-row target date + title.
    const payload = selectedTaskIds.map((taskId) => ({
      id: taskId,
      targetItems: (Array.isArray(copyRows[taskId]) ? copyRows[taskId] : []).map((row) => ({
        targetDate: row.targetDate,
        newTitle: row.newTitle.trim(),
      })),
    }));

    axios
      .post(`${MAIN_URL}/api/project-management/project-tasks/mass-copy`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        // Show toast and redirect to /project-task
        setToast({ open: true, message: "Tasks copied successfully!", severity: "success" });
        setIsConfirmOpen(false);

        setTimeout(() => {
          navigate("/project-task");
        }, 1500);

        if (typeof onNext === "function") {
          onNext(payload, response.data?.data);
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Failed to copy tasks. Please try again.";
        alert(msg);
        setIsConfirmOpen(false);
      });
  };

  const activeTask = tasks.find(
    (task) => task.organization_project_task_id === activeConfigTaskId
  );
  const activeRows = Array.isArray(copyRows[activeConfigTaskId])
    ? copyRows[activeConfigTaskId]
    : [];
  const selectedCalendarDates = activeRows
    .map((row) => row?.targetDate)
    .filter(Boolean);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ mb: 1 }}
            >
              <Link underline="hover" color="inherit">
                Project Tasks
              </Link>
              <Typography color="text.primary"></Typography>
            </Breadcrumbs>
            <Typography variant="h5" fontWeight={600}>
              Mass Copy
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Define the copy scope and select tasks to duplicate.
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mt: 4, borderRadius: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            FILTER SOURCE TASKS
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                sx={{ width: 300 }}
                InputProps={{
                  sx: { height: 56 }
                }}
                select
                fullWidth
                label="Project"
                SelectProps={{
                  multiple: true,
                  value: selectedOptions.Project,
                  onChange: (e) => {
                    const raw = e.target.value;
                    const next = (typeof raw === "string" ? raw.split(",") : raw)
                      .map((v) => Number(v))
                      .filter((v) => Number.isFinite(v));
                    setSelectedOptions((prev) => ({
                      ...prev,
                      Project: next,
                    }));
                  },
                }}
              >
                {selectOptions.Project.map((project) => (
                  <MenuItem
                    key={project.organization_project_id}
                    value={project.organization_project_id}
                  >
                    <Checkbox
                      checked={selectedOptions.Project.includes(
                        project.organization_project_id
                      )}
                    />
                    {project.project_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Employee Type Filter */}
              <TextField
                select
                sx={{ width: 300 }}
                label="Employee Type"
                value={employeeType}
                onChange={(e) => {
                  setEmployeeType(e.target.value);
                  // Whenever employeeType changes, Employees selection should be cleared
                  setSelectedOptions((prev) => ({ ...prev, Employees: [] }));
                }}
                InputProps={{
                  sx: { height: 56 }
                }}
              >
                <MenuItem value="1">Employee</MenuItem>
                <MenuItem value="2">Intern</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Employee/Intern Filter */}
              <TextField
                select
                sx={{ width: 300 }}
                InputProps={{
                  sx: { height: 56 }
                }}
                label={employeeType === "1" ? "Employees" : "Interns"}
                SelectProps={{
                  multiple: true,
                  value: selectedOptions.Employees,
                  onChange: (e) => {
                    const raw = e.target.value;
                    const next = (typeof raw === "string" ? raw.split(",") : raw)
                      .map((v) => Number(v))
                      .filter((v) => Number.isFinite(v));
                    setSelectedOptions((prev) => ({
                      ...prev,
                      Employees: next,
                    }));
                  },
                }}
              >
                {selectOptions.Employees.map((person) => (
                  <MenuItem
                    key={person.organization_person_id}
                    value={person.organization_person_id}
                  >
                    <Checkbox
                      checked={selectedOptions.Employees.includes(
                        person.organization_person_id
                      )}
                    />
                    {person.first_name} {person.last_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" mb={1}>
                Source Period
              </Typography>

              <ToggleButtonGroup
                value={selectedOptions.timePeriod}
                exclusive
                onChange={(e, val) => {
                  if (!val) return;
                  setSelectedOptions((prev) => ({
                    ...prev,
                    timePeriod: val,
                  }));
                }}
                size="small"
              >
                <ToggleButton value="lastWeek">Last Week</ToggleButton>
                <ToggleButton value="lastMonth">Last Month</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>

              {selectedOptions.timePeriod === "custom" && (
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <TextField
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    value={customDates.from}
                    onChange={(e) =>
                      setCustomDates((prev) => ({
                        ...prev,
                        from: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    value={customDates.to}
                    onChange={(e) =>
                      setCustomDates((prev) => ({
                        ...prev,
                        to: e.target.value,
                      }))
                    }
                  />
                </Box>
              )}

              <Button
                variant="contained"
                sx={{ ml: 2 }}
                onClick={fetchTasks}
              >
                Go
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper sx={{ mt: 4, borderRadius: 3 }}>
          <Divider />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  {/* <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedTaskIds.length > 0 && selectedTaskIds.length < tasks.length}
                      checked={tasks.length > 0 && selectedTaskIds.length === tasks.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const allIds = tasks.map((task) => task.organization_project_task_id);
                          setSelectedTaskIds(allIds);
                        } else {
                          setSelectedTaskIds([]);
                          setTargetDates({});
                        }
                      }}
                      inputProps={{
                        "aria-label": "select all tasks"
                      }}
                    />
                  </TableCell> */}

                  <TableCell>Task Name</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Source Date</TableCell>
                  <TableCell>Copy Entries</TableCell>
                  <TableCell align="right">Mins</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.organization_project_task_id ?? task.id} hover>
                    <TableCell>
                      <Checkbox
                     
                        disabled={Array.isArray(task.schedules) && task.schedules.length > 1}
                        checked={selectedTaskIds.includes(task.organization_project_task_id)}
                        onChange={() => handleToggleTask(task.organization_project_task_id)}
                      />
                      {Array.isArray(task.schedules) && task.schedules.length > 1 && (
                        <Tooltip title="Task has multiple schedules, cannot be copied" placement="right" arrow>
                          <InfoOutlinedIcon color="info" fontSize="small" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {task.task_title}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Avatar sx={{ width: 28, height: 28 }} />
                        <Typography variant="body2">
                          {task.assigned_organization_person.person.full_name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.project?.project_name || "-"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {task.scheduled_date}
                    </TableCell>
                    <TableCell>
                      {/* Copy rows are configured in popup */}
                      {selectedTaskIds.includes(task.organization_project_task_id) && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {copyRows[task.organization_project_task_id]?.length || 0} row(s)
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              openTaskConfig(task.organization_project_task_id)
                            }
                          >
                            Select
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {task.total_estimated_minutes}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon color="primary" />
            Configure Copy Rows {activeTask ? `- ${activeTask.task_title}` : ""}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {activeTask?.task_title || "Task"} ({activeTask?.scheduled_date || "-"})
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Box sx={{ border: "1px solid #f0f0f0", borderRadius: 2, p: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Click dates to select/unselect multiple target dates
                  </Typography>
                  <DateCalendar
                    value={calendarViewDate}
                    onChange={(newValue) => {
                      if (!newValue) return;
                      setCalendarViewDate(newValue);
                      handleToggleCalendarDate(activeConfigTaskId, newValue);
                    }}
                    shouldDisableDate={(date) => {
                      const minDate = parseScheduledDate(activeTask?.scheduled_date);
                      return minDate ? dayjs(date).isBefore(minDate, "day") : false;
                    }}
                    slots={{
                      day: (dayProps) => {
                        const isSelected = selectedCalendarDates.some((selectedDate) =>
                          dayjs(selectedDate).isSame(dayProps.day, "day")
                        );
                        return <PickersDay {...dayProps} selected={isSelected} />;
                      },
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 340 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Task title selector
                  </Typography>
                  {!activeRows.length && (
                    <DialogContentText sx={{ mb: 1 }}>
                      Select one or more dates from the calendar to add copy rows.
                    </DialogContentText>
                  )}
                  {activeRows.map((row, idx) => (
                    <Box key={`${activeConfigTaskId}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        label="Target Date"
                        value={row?.targetDate || ""}
                        sx={{ width: 160 }}
                        InputProps={{ readOnly: true }}
                      />
                      <TextField
                        size="small"
                        label="New Task Title"
                        value={row?.newTitle || ""}
                        onChange={(e) =>
                          handleCopyRowChange(
                            activeConfigTaskId,
                            idx,
                            "newTitle",
                            e.target.value
                          )
                        }
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        aria-label="add-copy-row"
                        size="small"
                        onClick={() => handleAddCopyRow(activeConfigTaskId)}
                      >
                        <AddCircleOutlineIcon fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton
                        aria-label="remove-copy-row"
                        size="small"
                        onClick={() => handleRemoveCopyRow(activeConfigTaskId, idx)}
                        disabled={activeRows.length <= 1}
                      >
                        <RemoveCircleOutlineIcon
                          fontSize="small"
                          color={activeRows.length <= 1 ? "disabled" : "error"}
                        />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: '16px 24px' }}>
            <Button onClick={() => setIsConfirmOpen(false)} variant="outlined">Done</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={2000}
          onClose={() => setToast((prev) => ({...prev, open: false}))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setToast((prev) => ({...prev, open: false}))} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>

        {/* Footer */}
        <Box
          mt={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!areAllCopyRowsValid()}
            onClick={handleConfirmCopy}
          >
            Copy
          </Button>
        </Box>
      </Box>
    </Container>
  );
}